import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
} from '@angular/core';
import {
  TuiInputModule,
  TuiInputNumberModule,
  TuiToggleModule,
} from '@taiga-ui/kit';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MaskitoModule } from '@maskito/angular';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiDialogService,
  TuiGroupModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { Store } from '@ngxs/store';
import {
  POLYMORPHEUS_CONTEXT,
  PolymorpheusComponent,
} from '@tinkoff/ng-polymorpheus';
import { AlertService } from '@shared/services/alert.service';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { AsyncPipe, NgForOf } from '@angular/common';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FarmService } from '@shared/services/farm.service';
import { FlowerTypeService } from '@shared/services/flower-type.service';
import { MultiSelectComponent } from '@shared/components/forms/multi-select/multi-select.component';
import { CURRENCIES } from '@shared/constants/currency';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import CatalogsState from '@store/catalogs/catalogs.state';
import { FormsService } from '@shared/services/forms.service';
import {
  LocationSelectComponent,
  LocationSelectInputData,
  LocationSelectOutputData,
} from '@shared/components/google-map/location-select/location-select.component';
import { joinLatLong, splitLatLong } from '@shared/utils/coords';
import { OrganizationMemberComponent } from '@shared/components/catalogs/organization-member/organization-member.component';
import {
  Farm,
  FarmEditAddressForm,
  FarmEditForm,
} from '../../../../types/farm';
import {
  OrganizationMemberContactForm,
  OrganizationMemberForm,
} from '../../../../types/organization-member';

export type FarmFormInputData = {
  farm?: Nullable<Farm>;
};

@Component({
  selector: 'app-farm-form',
  standalone: true,
  imports: [
    TuiInputModule,
    ReactiveFormsModule,
    MaskitoModule,
    TuiButtonModule,
    AsyncPipe,
    AutocompleteComponent,
    TuiToggleModule,
    TuiTextfieldControllerModule,
    MultiSelectComponent,
    TuiGroupModule,
    TuiInputNumberModule,
    CheckboxComponent,
    NgForOf,
    OrganizationMemberComponent,
  ],
  templateUrl: './farm-form.component.html',
  styleUrl: './farm-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FarmFormComponent implements OnInit {
  save$ = new Subject<void>();
  selectLocation$ = new Subject<number>();
  isEdit$ = new BehaviorSubject(false);
  title$ = this.isEdit$.pipe(
    map(value => (value ? 'Редактировать' : 'Создать').concat(' питомник')),
  );

  farmForm = this.fb.group<FarmEditForm>({
    id: this.fb.control<Nullable<number>>(null),
    uniqueKey: this.fb.control(null),
    flowerTypesJson: this.fb.nonNullable.control<string[]>([]),
    name: this.fb.control<Nullable<string>>(null, Validators.required),
    nameFromInvoice: this.fb.control(null),
    imported: this.fb.control<Nullable<boolean>>(null),
    precoolingRate: this.fb.control(null),
    currencyLetter: this.fb.control(null),
    publicAccess: this.fb.control(null),
    addressesJson: this.fb.array<FormGroup<FarmEditAddressForm>>([]),
    broker: this.fb.control(null),
    taxNonPayer: this.fb.control(null),
    members: this.fb.array<FormGroup<OrganizationMemberForm>>([]),
    comment: this.fb.control(null),
  });

  countries = this.store.selectSignal(CatalogsState.getNsiCountries);

  flowerTypes$ = this.flowerTypeService.getAll().pipe(shareReplay());

  protected readonly CURRENCIES = CURRENCIES;
  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    protected readonly context: TuiDialogContext<void, FarmFormInputData>,
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly destroyRef: DestroyRef,
    private readonly alert: AlertService,
    private readonly farmService: FarmService,
    private readonly flowerTypeService: FlowerTypeService,
    private readonly dialogs: TuiDialogService,
    private readonly forms: FormsService,
  ) {}

  onSaveClick() {
    this.save$
      .pipe(
        filter(() => this.farmForm.valid),
        switchMap(() => {
          const { addressesJson, ...value } = this.farmForm.getRawValue();
          const farm: Farm = {
            ...value,
            addressesJson: addressesJson.map(addressJson => {
              return {
                ...addressJson,
                ...splitLatLong(addressJson.latLng),
              };
            }),
          };
          return this.farmService.save(farm);
        }),
        tap(() => this.closeOnSave()),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onLocationSelect() {
    this.selectLocation$
      .pipe(
        switchMap(index => {
          const data: LocationSelectInputData = {
            coords: this.farmForm.getRawValue().addressesJson[index].latLng,
          };
          return this.dialogs
            .open<LocationSelectOutputData>(
              new PolymorpheusComponent(LocationSelectComponent),
              {
                size: 'auto',
                data,
              },
            )
            .pipe(
              tap(({ latLng, address }) => {
                const addressForm =
                  this.farmForm.controls.addressesJson.controls.at(index);

                addressForm?.patchValue({
                  latLng: joinLatLong(
                    latLng.lat.toString(),
                    latLng.lng.toString(),
                  ),
                  address,
                });
              }),
            );
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  closeOnSave() {
    this.context.$implicit.next();
    this.context.$implicit.complete();
  }

  addAddress() {
    const group = this.forms.getFarmEditAddressForm();
    group.controls.address.addValidators(Validators.required);
    group.controls.address.markAsTouched();
    this.farmForm.controls.addressesJson.push(group);
  }

  removeAddress(index: number) {
    this.farmForm.controls.addressesJson.removeAt(index);
  }

  setForm() {
    const farmJson = this.context.data?.farm;
    if (!farmJson) {
      return;
    }
    const { addressesJson, members, ...farm } = farmJson;
    this.farmForm.patchValue(farm);
    this.farmForm.controls.addressesJson.reset();

    this.farmForm.setControl(
      'members',
      this.fb.array(
        members.map(({ contacts, ...member }) => {
          const form = this.createMemberForm();
          form.patchValue(member);
          form.setControl(
            'contacts',
            this.fb.array(contacts.map(c => this.fb.group(c))),
          );
          return form;
        }),
      ),
    );

    addressesJson.forEach((farmAddress, index, items) => {
      const addressForm = this.forms.getFarmEditAddressForm(farmAddress);
      addressForm.controls.address.addValidators(Validators.required);
      this.farmForm.controls.addressesJson.push(addressForm, {
        emitEvent: index === items.length - 1,
      });
    });
  }

  removeMember(index: number) {
    this.farmForm.controls.members.removeAt(index);
  }

  createMemberContactForm() {
    return this.fb.group<OrganizationMemberContactForm>({
      type: this.fb.control(null),
      value: this.fb.control(null),
    });
  }

  createMemberForm() {
    return this.fb.group<OrganizationMemberForm>({
      name: this.fb.control(null),
      position: this.fb.control(null),
      contacts: this.fb.array<FormGroup<OrganizationMemberContactForm>>([]),
    });
  }

  addMember() {
    const form = this.createMemberForm();
    form.controls.contacts.push(this.createMemberContactForm());
    this.farmForm.controls.members.push(form);
  }

  ngOnInit() {
    this.isEdit$.next(!!this.context.data?.farm);
    this.setForm();
    this.onSaveClick();
    this.onLocationSelect();
  }
}
